import {
  CONTRACT_NETWORK,
  CONTRACTS,
  DEFAULT_CONTRACT_ADDRESS,
  API_BASE_URL,
  ENS_API_BASE_URL,
} from "./constants";
import * as local from "./local.js";
import { encodingToHex } from "bitframes";

export const LEADER_VISIBLE_LENGTH = 20;

const ONE_SECOND = 1000;
const ONE_MINUTE = 60 * ONE_SECOND;
const ONE_HOUR = 60 * ONE_MINUTE;
const ONE_DAY = 24 * ONE_HOUR;

function withCache({
  fetch,
  getKey,
  timeout = ONE_MINUTE,
  withNetworkPrefix = true,
} = {}) {
  const toKey = (opts = {}) => {
    let cacheKey = getKey(opts);
    if (withNetworkPrefix && !cacheKey.startsWith(CONTRACT_NETWORK)) {
      cacheKey = CONTRACT_NETWORK + ":" + cacheKey;
    }
    return cacheKey;
  };
  const pendingFetches = new Map();
  const fetcher = async function fetchData(opts) {
    const cacheKey = toKey(opts);
    const now = Date.now();

    // Check if data is in cache and not expired
    // console.time("localforage");
    const cachedData = local.get(cacheKey);
    // console.timeEnd("localforage");
    if (cachedData && now - cachedData.timestamp < timeout) {
      return cachedData.data;
    }

    if (pendingFetches.has(cacheKey)) {
      // Return the in-progress fetch promise
      return pendingFetches.get(cacheKey);
    }

    // Start a new fetch and store the promise
    const fetchPromise = (async () => {
      try {
        const data = await fetch(opts);
        await local.set(cacheKey, { data, timestamp: now });
        return data;
      } finally {
        // Remove the promise from pending fetches once resolved or rejected
        pendingFetches.delete(cacheKey);
      }
    })();

    pendingFetches.set(cacheKey, fetchPromise);
    return fetchPromise;
  };
  fetcher.clearCache = (opts = {}) => {
    const cacheKey = toKey(opts);
    local.remove(cacheKey);
  };
  return fetcher;
}

export const fetchENSAddress = withCache({
  fetch: async ({ address }) => {
    const url = new URL(ENS_API_BASE_URL + "/ens");
    url.searchParams.append("address", address);
    const resp = await fetch(url.href, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (resp.status !== 200) return null;
    return resp.json();
  },
  withNetworkPrefix: false,
  getKey: ({ address }) => `ens-${address.toLowerCase()}`,
  timeout: ONE_DAY,
});

export const fetchLeaders = withCache({
  fetch: async () => {
    const url = new URL(API_BASE_URL + "/leaders");
    url.searchParams.append("length", LEADER_VISIBLE_LENGTH);
    const resp = await fetch(url.href, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (resp.status !== 200) return null;
    return resp.json();
  },
  getKey: () => `leaders`,
  timeout: ONE_MINUTE,
});

export const fetchContributor = withCache({
  fetch: async ({ address }) => {
    const url = new URL(API_BASE_URL + "/contributor");
    url.searchParams.append("address", address);
    const resp = await fetch(url.href, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (resp.status !== 200) return null;
    return resp.json();
  },
  getKey: ({ address }) => `contributor-${address}`,
  timeout: ONE_MINUTE,
});

export const fetchReceipt = withCache({
  fetch: async ({ tx_hash }) => {
    const url = new URL(API_BASE_URL + "/receipt");
    url.searchParams.append("tx_hash", tx_hash);
    const resp = await fetch(url.href, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (resp.status !== 200) return null;
    return resp.json();
  },
  getKey: ({ tx_hash }) => `receipt-${tx_hash}`,
  timeout: 2 * ONE_MINUTE, // a little longer...
});

export const fetchLatestOpenEditionMints = () => {
  return fetchTokens({
    order: "desc",
    length: 4,
  });
};

export const fetchOpenEditionTokenCount = async () => {
  const res = await fetchLatestOpenEditionMints();
  if (res && res.length) {
    return res.reduce((a, b) => Math.max(a, b.token_id), 0);
  }
  return 0;
};

export const fetchTokens = withCache({
  fetch: async ({
    offset,
    length,
    publisher,
    compact = true,
    binary = true,
    order = "asc",
    contract = DEFAULT_CONTRACT_ADDRESS,
  }) => {
    const url = new URL(API_BASE_URL + "/tokens");
    url.searchParams.append("compact", compact);
    if (offset) url.searchParams.append("offset", offset);
    if (length) url.searchParams.append("length", length);
    if (order) url.searchParams.append("order", order);
    if (contract) url.searchParams.append("contract", contract);
    if (publisher) url.searchParams.append("publisher", publisher);
    if (binary) url.searchParams.append("binary", binary);
    console.log(url);
    const resp = await fetch(url.href, {
      method: "GET",
      headers: {
        "Content-Type":
          binary && compact ? "application/octet-stream" : "application/json",
      },
    });
    if (compact && binary) {
      const buf = await resp.arrayBuffer();
      const dv = new DataView(buf);
      let off = 0;
      const count = dv.getUint16(off);
      off += 2;
      const tokens = [];
      for (let i = 0; i < count; i++) {
        const token_id = dv.getUint16(off);
        off += 2;
        const bytes = new Uint8Array(32);
        for (let b = 0; b < bytes.length; b++) {
          bytes[b] = dv.getUint8(off++);
        }

        let seed;
        try {
          seed = encodingToHex(bytes);
          if (seed && contract === CONTRACTS.NFT_LIMITED.address) {
            // this is a hack for the dev version, which had a buggy token on Sepolia that should not be
            // possible on mainnet due to the upgraded contract
            seed = fixedSeed(seed);
          }
        } catch (err) {
          console.warn(err);
        }

        if (isFinite(token_id) && seed) {
          tokens.push({ token_id, seed });
        }
      }
      return tokens;
    } else if (compact) {
      const strResult = (await resp.text()).trim();
      if (strResult.length === 0 || resp.status !== 200) {
        return [];
      } else {
        return strResult
          .split("\n")
          .map((n) => {
            let [token_id, seed] = n.split(",");
            if (seed && contract === CONTRACTS.NFT_LIMITED.address) {
              // this is a hack for the dev version, which had a buggy token on Sepolia that should not be
              // possible on mainnet due to the upgraded contract
              seed = fixedSeed(seed);
            }
            return { token_id: parseInt(token_id, 10), seed };
          })
          .filter((t) => isFinite(t.token_id) && t.seed);
      }
    } else {
      const tokens = await resp.json();
      return tokens.map((token) => {
        token = { ...token };
        const needsFix =
          (token.seed && contract === CONTRACTS.NFT_LIMITED.address) ||
          token.contract === CONTRACTS.NFT_LIMITED.address;
        if (needsFix) {
          token.seed = fixedSeed(token.seed);
        }
        return token;
      });
    }
  },
  getKey: ({
    offset,
    length,
    publisher,
    compact = true,
    order,
    contract = DEFAULT_CONTRACT_ADDRESS,
  }) =>
    `page-offset:${offset}-len:${length}-publisher:${publisher || "all"}-order:${order}-contract:${contract}-compact:${compact}`,
  timeout: ONE_MINUTE,
});

export const fetchTokenDetails = withCache({
  fetch: async ({ token_id, contract = DEFAULT_CONTRACT_ADDRESS }) => {
    const url = new URL(API_BASE_URL + "/token");
    url.searchParams.append("token_id", token_id);
    if (contract) url.searchParams.append("contract", contract);
    const resp = await fetch(url.href, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (resp.status !== 200) return null;
    const result = await resp.json();
    if (result && result.seed && contract === CONTRACTS.NFT_LIMITED.address) {
      // this is a hack for the dev version, which had a buggy token on Sepolia that should not be
      // possible on mainnet due to the upgraded contract
      result.seed = fixedSeed(result.seed);
    }
    return result;
  },
  getKey: ({ token_id, contract = DEFAULT_CONTRACT_ADDRESS }) =>
    `token-${token_id}-contract:${contract}`,
  timeout: ONE_MINUTE,
});

function fixedSeed(seed) {
  return seed.replace(/^00/, "01");
}
